import { webpSrc, srcSetFor, bestSrcFor, SIZES } from "@/lib/webpSrc";

/**
 * Drop-in replacement for `<img>` that:
 *  1. Automatically swaps the src to the best WebP variant we have on disk
 *  2. Emits a responsive `srcSet` if multi-size variants exist for this file
 *  3. Applies a sensible default `sizes` (100vw hero-style — override via prop)
 *  4. Preserves every native `<img>` attribute (className, style, alt, onClick,
 *     loading, decoding, fetchPriority etc.) via prop spread
 *
 *   <Img src="/assets/karls/hero-architecture.jpg" alt="…" sizes="100vw" />
 *
 *   Result on mobile (390 px wide, DPR 3):
 *     browser picks hero-architecture-960w.webp (~160 KB)
 *     instead of the full hero-architecture.webp (~800 KB)  → ~640 KB saved
 *
 * Falls back gracefully:
 *   - For images not in the responsive manifest, just emits <img src={webpSrc(src)}>
 *   - For external URLs (Pexels, Contentful, wa.me) — passes through untouched
 */
export default function Img({ src, sizes = SIZES.hero, alt = "", ...rest }) {
  if (!src || !src.startsWith("/assets/")) {
    // External / non-asset URL — just render a normal img
    return <img src={src} alt={alt} {...rest} />;
  }

  const srcSet = srcSetFor(src);
  const finalSrc = srcSet ? bestSrcFor(src) : webpSrc(src);

  return (
    <img
      src={finalSrc}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      {...rest}
    />
  );
}
